import React, { useEffect, useState } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { Card, Typography, Stack, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

import JumboSearch from "@jumbo/components/JumboSearch";
import Div from "@jumbo/shared/Div/Div";
import { getCustomer, setBackendDomain } from "app/services/api/management";

import jwtAuthAxiosKiosk from "app/services/auth/jwtAuthKiosk";
import ProductItemModal from "./ProductItemModal";

const ProductSelector = ({
  setFieldValue,
  values,
  setCloseModal,
  categoryId,
  products: productsFromProps,
}) => {
  const { getAuthUser } = useJumboAuth();
  const {
    customer: { url },
  } = getAuthUser();

  const { t, i18n } = useTranslation();
  const [searchName, setSearchName] = useState();

  const [products, setProducts] = useState(productsFromProps || []);
  const [defaultProducts, setDefaultProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState();
  // Bulk selection: map of productId -> product object.
  const [selected, setSelected] = useState({});
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const customerUrl = url || "";

  const loadProducts = async () => {
    try {
      const sourceProducts =
        defaultProducts.length > 0 ? defaultProducts : productsFromProps || [];
      const q =
        typeof searchName === "string" ? searchName.trim().toLowerCase() : "";
      const filteredProducts = q
        ? sourceProducts.filter((product) => {
            const localized =
              product?.productName?.[defaultLanguage] ||
              product?.metadata?.name?.[defaultLanguage] ||
              product?.productName ||
              product?.metadata?.name ||
              product?.name ||
              "";
            const nameStr = String(localized).toLowerCase();
            return nameStr.includes(q);
          })
        : sourceProducts;

      setProducts(filteredProducts || []);
      if (defaultProducts.length === 0) {
        setDefaultProducts(sourceProducts);
      }
    } catch (error) {
      setProducts([]);
      setDefaultProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const searchByName = async (searchName) => {
    setLoading(true);
    setBackendDomain(jwtAuthAxiosKiosk);
    if (!jwtAuthAxiosKiosk.defaults.baseURL) {
      await loadCustomer();
    }
    // Always load (empty query shows all)
    await loadProducts();
  };

  useEffect(() => {
    searchByName(searchName);
  }, [searchName, defaultLanguage, productsFromProps]);

  const loadCustomer = async () => {
    await getCustomer(customerUrl).then((customer) => setCustomer(customer));
  };

  useEffect(() => {
    loadCustomer();
  }, []);

  useEffect(() => {
    if (productsFromProps && productsFromProps.length > 0) {
      setDefaultProducts(productsFromProps);
      setProducts(productsFromProps);
    }
  }, [productsFromProps]);

  // Products already assigned to this category (cannot be selected again).
  const isAlreadyAdded = (productId) =>
    Boolean(values && values.products && values.products[productId]);

  const toggleSelect = (product) => {
    if (!product || product._id == null) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[product._id]) delete next[product._id];
      else next[product._id] = product;
      return next;
    });
  };

  // Select every currently visible (filtered) product that is not already in the category.
  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = { ...prev };
      (products || []).forEach((p) => {
        if (p && p._id != null && !isAlreadyAdded(p._id)) next[p._id] = p;
      });
      return next;
    });
  };

  const clearSelection = () => setSelected({});

  const selectedCount = Object.keys(selected).length;

  // Merge all selected products into the category at once, then close the modal.
  const addSelected = () => {
    if (selectedCount === 0) return;
    if (setFieldValue) {
      const currentProducts = (values && values.products) || {};
      setFieldValue("products", { ...currentProducts, ...selected });
    }
    if (setCloseModal) {
      setCloseModal();
    }
  };

  return (
    <React.Fragment>
      <Div
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          pb: 2,
          mb: 1,
        }}
      >
        <Typography variant={"h2"} mb={2}>
          {t("pages.dsokProducts.title")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Card sx={{ mb: 0, width: { xs: "100%", sm: "45%" } }}>
            <JumboSearch
              page="DSOKProducts"
              onChange={setSearchName}
              sx={{ width: "100%" }}
              placeholder={t("pages.presentation.searchPresentation")}
            />
          </Card>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Button
              size="small"
              variant="text"
              onClick={selectAllVisible}
              disabled={loading || !products?.length}
            >
              {t("buttons.selectAllVisible", { defaultValue: "Vybrať zobrazené" })}
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={clearSelection}
              disabled={selectedCount === 0}
            >
              {t("buttons.clearSelection", { defaultValue: "Zrušiť výber" })}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addSelected}
              disabled={selectedCount === 0}
            >
              {t("buttons.addSelected", { defaultValue: "Pridať vybrané" })} (
              {selectedCount})
            </Button>
          </Stack>
        </Stack>
      </Div>
      {loading ? (
        <Div sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {t("common.loading")}...
          </Typography>
        </Div>
      ) : products?.length ? (
        <Div sx={{ mb: 2 }}>
          {products.map((product, index) => (
            <ProductItemModal
              key={product._id || index}
              item={product}
              disabled={loading}
              selected={Boolean(product._id != null && selected[product._id])}
              onToggleSelect={toggleSelect}
              alreadyAdded={isAlreadyAdded(product._id)}
              productLanguages={languages}
              defaultLanguage={defaultLanguage}
              searchName={searchName}
            />
          ))}
        </Div>
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default ProductSelector;
